'use client';

/**
 * usePushNotifications Hook
 *
 * Encapsulates the full push notification lifecycle: browser support detection,
 * permission request, FCM token retrieval, token refresh handling, foreground
 * message delivery, and backend registration/revocation.
 *
 * Firebase SDK is dynamically imported to avoid loading it in all portals
 * that import design-system-ui.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export interface UsePushNotificationsOptions {
  /** Callback to register token with backend */
  onRegisterToken: (data: { fcmToken: string; deviceFingerprint: string; platform: string }) => Promise<unknown>;
  /** Callback to revoke token from backend */
  onRevokeToken: (data: { deviceFingerprint: string }) => Promise<unknown>;
  /** Device fingerprint for current browser */
  deviceFingerprint: string;
  /** Firebase config for getToken */
  firebaseConfig: Record<string, string>;
  /** VAPID key for web push */
  vapidKey: string;
  /** Optional callback for foreground messages (Gap 4) */
  onForegroundMessage?: (payload: { title?: string; body?: string; data?: Record<string, string> }) => void;
}

export interface UsePushNotificationsReturn {
  /** Whether browser supports push notifications */
  isSupported: boolean;
  /** Current permission state */
  permissionState: PushPermissionState;
  /** Request permission and register token */
  requestPermission: () => Promise<boolean>;
  /** Revoke token */
  revokePermission: () => Promise<void>;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

/** Dynamically import Firebase modules (avoids design-system-ui build-time dependency) */
async function loadFirebaseModules() {
  const FIREBASE_MESSAGING_MODULE = 'firebase/messaging';
  const FIREBASE_APP_MODULE = 'firebase/app';
  const firebaseMessaging = await import(/* webpackIgnore: true */ FIREBASE_MESSAGING_MODULE);
  const firebaseApp = await import(/* webpackIgnore: true */ FIREBASE_APP_MODULE);
  return { firebaseMessaging, firebaseApp };
}

/** Get or initialize the Firebase app */
function getOrInitApp(
  firebaseApp: { getApps: () => unknown[]; initializeApp: (config: Record<string, string>) => unknown },
  config: Record<string, string>,
) {
  return firebaseApp.getApps().length > 0 ? firebaseApp.getApps()[0] : firebaseApp.initializeApp(config);
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to manage push notification lifecycle
 *
 * @example
 * ```tsx
 * const { isSupported, permissionState, requestPermission, revokePermission, isLoading, error } =
 *   usePushNotifications({
 *     onRegisterToken: async (data) => trpc.notification.registerDevice.mutate(data),
 *     onRevokeToken: async (data) => trpc.notification.revokeDevice.mutate(data),
 *     deviceFingerprint: 'browser-xyz',
 *     firebaseConfig: { apiKey: '...', projectId: '...' },
 *     vapidKey: 'BPx...',
 *     onForegroundMessage: (payload) => showToast(payload.title, payload.body),
 *   });
 *
 * if (permissionState === 'default') {
 *   return <GlassButton onClick={requestPermission}>Enable Notifications</GlassButton>;
 * }
 * ```
 */
export function usePushNotifications(options: UsePushNotificationsOptions): UsePushNotificationsReturn {
  const { onRegisterToken, onRevokeToken, deviceFingerprint, vapidKey, onForegroundMessage } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;

  const [permissionState, setPermissionState] = useState<PushPermissionState>(
    isSupported ? (Notification.permission as PushPermissionState) : 'unsupported',
  );

  // Refs for token refresh and foreground message handling (Gap 3)
  const currentTokenRef = useRef<string | null>(null);
  const messagingRef = useRef<unknown>(null);
  const unsubscribeMessageRef = useRef<(() => void) | null>(null);
  const onForegroundMessageRef = useRef(onForegroundMessage);
  const onRegisterTokenRef = useRef(onRegisterToken);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    onForegroundMessageRef.current = onForegroundMessage;
  }, [onForegroundMessage]);

  useEffect(() => {
    onRegisterTokenRef.current = onRegisterToken;
  }, [onRegisterToken]);

  // Sync permission state on mount
  useEffect(() => {
    if (isSupported) {
      setPermissionState(Notification.permission as PushPermissionState);
    }
  }, [isSupported]);

  // ── Gap 3: Token refresh + Gap 4: Foreground message listener ───────────
  // When permission is already granted, initialize Firebase, set up onMessage
  // listener, and monitor token freshness via visibilitychange events.
  useEffect(() => {
    if (!isSupported || permissionState !== 'granted') return;

    let cancelled = false;

    async function setupMessagingAndTokenRefresh() {
      try {
        const { firebaseMessaging, firebaseApp } = await loadFirebaseModules();
        if (cancelled) return;

        const app = getOrInitApp(firebaseApp, options.firebaseConfig);
        const messaging = firebaseMessaging.getMessaging(app);
        messagingRef.current = messaging;

        // Get current token and store in ref
        const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        if (cancelled) return;

        const token: string | undefined = await firebaseMessaging.getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: swRegistration,
        });

        if (cancelled) return;
        if (token) {
          currentTokenRef.current = token;
        }

        // Gap 4: Set up foreground message listener
        const unsubscribe = firebaseMessaging.onMessage(
          messaging,
          (payload: { notification?: { title?: string; body?: string }; data?: Record<string, string> }) => {
            if (onForegroundMessageRef.current) {
              onForegroundMessageRef.current({
                title: payload.notification?.title,
                body: payload.notification?.body,
                data: payload.data,
              });
            }
          },
        );
        unsubscribeMessageRef.current = unsubscribe;

        // Gap 3: Token refresh via visibilitychange
        // When the tab becomes visible, check if the token has changed
        const handleVisibilityChange = async () => {
          if (document.visibilityState !== 'visible' || cancelled) return;

          try {
            const freshToken: string | undefined = await firebaseMessaging.getToken(messaging, {
              vapidKey,
              serviceWorkerRegistration: swRegistration,
            });

            if (cancelled || !freshToken) return;

            if (freshToken !== currentTokenRef.current) {
              currentTokenRef.current = freshToken;
              await onRegisterTokenRef.current({
                fcmToken: freshToken,
                deviceFingerprint,
                platform: 'web',
              });
            }
          } catch {
            // Silently ignore refresh check failures — next visibility change will retry
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Extend cleanup to remove the visibility listener
        const originalUnsubscribe = unsubscribeMessageRef.current;
        unsubscribeMessageRef.current = () => {
          originalUnsubscribe?.();
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
      } catch {
        // Firebase initialization may fail in SSR or restricted environments — ignore
      }
    }

    setupMessagingAndTokenRefresh();

    return () => {
      cancelled = true;
      unsubscribeMessageRef.current?.();
      unsubscribeMessageRef.current = null;
    };
  }, [isSupported, permissionState, vapidKey, deviceFingerprint, options.firebaseConfig]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications not supported in this browser');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission as PushPermissionState);

      if (permission !== 'granted') {
        setError('Notification permission denied. Enable in browser settings.');
        return false;
      }

      // Dynamically import Firebase Messaging to avoid loading it upfront.
      // Firebase is a peer dependency — types are not available at design-system-ui build time.
      // We use variable-based import() to prevent TypeScript DTS generation from resolving the module.
      const { firebaseMessaging, firebaseApp } = await loadFirebaseModules();

      // Initialize Firebase app if not already done
      const app = getOrInitApp(firebaseApp, options.firebaseConfig);
      const messaging = firebaseMessaging.getMessaging(app);
      messagingRef.current = messaging;

      // Register service worker for background messages
      const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

      const fcmToken: string | undefined = await firebaseMessaging.getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (!fcmToken) {
        setError('Failed to get push notification token');
        return false;
      }

      // Store token for refresh detection (Gap 3)
      currentTokenRef.current = fcmToken;

      // Register with backend
      await onRegisterToken({
        fcmToken,
        deviceFingerprint,
        platform: 'web',
      });

      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to enable push notifications';
      setError(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, onRegisterToken, deviceFingerprint, vapidKey, options.firebaseConfig]);

  // Gap 6: Call Firebase deleteToken() before revoking from backend
  const revokePermission = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Unsubscribe from FCM first (Gap 6)
      try {
        const { firebaseMessaging, firebaseApp } = await loadFirebaseModules();
        if (firebaseApp.getApps().length > 0) {
          const messaging = firebaseMessaging.getMessaging(firebaseApp.getApps()[0]);
          await firebaseMessaging.deleteToken(messaging);
        }
      } catch {
        // Don't block revocation if Firebase cleanup fails
      }

      // Clean up foreground message listener
      unsubscribeMessageRef.current?.();
      unsubscribeMessageRef.current = null;
      currentTokenRef.current = null;
      messagingRef.current = null;

      // Then revoke from backend
      await onRevokeToken({ deviceFingerprint });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to revoke push token';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [onRevokeToken, deviceFingerprint]);

  return {
    isSupported,
    permissionState,
    requestPermission,
    revokePermission,
    isLoading,
    error,
  };
}
